<template>
  <div v-if="showNoPartnersMessage" class="w-full h-screen flex items-center justify-center overflow-hidden" style="max-height: calc(100vh - 140px)">
    <SpyderEmptyState
      class="mb-16"
      title="No partnership ads yet"
      :description="`Partnership ads run from a creator's handle instead of the brand's, which is exactly why they're easy to miss. The moment ${brand?.name ?? 'this brand'} launches one, it shows up here.`"
      :show-brands-link="false"
    />
  </div>
  <div v-else>
    <!-- View Toggle & Partner Suggestions -->
    <div class="w-full px-4 pt-4 pb-1 flex items-start justify-between">
      <!-- View Toggle -->
      <div class="w-max flex items-center gap-0.5 p-0.5 rounded-lg bg-neutral-25 mr-10">
        <button v-for="toggle in viewToggles" :key="`view-toggle-${toggle.value}`"
        class="toggle-atom flex items-center p-1 rounded-md" :class="{ 'active': toggle.active }"
        @click="currentView = toggle.value">
          <component :is="toggle.icon" class="transition-colors duration-100" :class="toggle.active ? 'text-text-muted' : 'text-text-subdued'" />
          <BaseText type="label" size="sm" class="px-1 transition-colors duration-100" :class="toggle.active ? 'text-text-muted' : 'text-text-normal'">
            {{ toggle.name }}
          </BaseText>
        </button>
      </div>
      <!-- Partner Suggestions -->
      <div v-if="currentView === 'ads'" class="flex items-center justify-end gap-2 flex-wrap" style="min-height: 32px;">
        <!-- Loading Skeleton -->
        <template v-if="loadingPartners">
          <div v-for="i in 5" class="skeleton w-24 h-7 rounded-lg" :key="`partner-skeleton-${i}`" />
        </template>
        <template v-else>
          <BaseTooltipV2 v-for="partner in partnerSuggestions" :key="`partner-suggestion-${partner.advertiserId}`"
          :tooltip-enabled="locked" text="Track this brand to unlock">
            <button class="flex items-center p-1 rounded-lg  fade-in transition-colors duration-100 "
            :class="locked ? 'bg-neutral-25 cursor-default' : 'border border-border-normal hover:border-border-hover hover:bg-neutral-25'"
            @click="addPartnerFilter(partner.advertiserId)" :disabled="locked">
              <div class="w-5 h-5 flex items-center justify-center" :class="{ 'opacity-60': locked }">
                <img v-if="partner.avatar" :src="partner.avatar" class="w-4 h-4 rounded-full">
                <DefaultAvatarIcon v-else :width="16" :height="16" />
              </div>
              <BaseText type="label" size="sm" class="px-1.5" :class="locked ? 'text-text-disabled' : 'text-text-muted'">
                {{ partner.displayName }}
              </BaseText>
            </button>
          </BaseTooltipV2>
          <div v-if="formattedPartners.length > partnerSuggestions.length" class="relative">
            <button class="p-1 rounded-lg fade-in transition-colors duration-100" :class="morePartnersButtonClasses" :disabled="locked"
            @click="showPartnerSelectionDropdown = !showPartnerSelectionDropdown" >
              <BaseText type="body" size="sm" class="px-0.5" :class="locked ? 'text-text-disabled' : 'text-text-muted'">
                +{{ locked ? 'more' : formattedPartners.length - partnerSuggestions.length }}
              </BaseText>
            </button>
            <transition>
              <div v-if="showPartnerSelectionDropdown && !locked" class="more-partners-dropdown" 
              v-on-clickaway="() => { showPartnerSelectionDropdown = false }">
                <FilterSelectDropdown 
                  :active-filters="activeFilters"
                  locked-category-id="partner_creators"
                  :custom-filter-categories="[partnersFilterCategory]"
                  @applyFilters="applyFilterChanges($event)"
                />
              </div>
            </transition>
          </div>
        </template>
      </div>
    </div>
    <!-- FILTER BAR -->
    <div v-if="currentView === 'ads'" ref="filterBar" class="spyder-filter-bar hidden sm:block bg-white bg-opacity-90 fade-in"
    style="backdrop-filter: blur(6px)">
      <FilterTopBar
        v-if="!locked"
        ref="filterTopBar"
        discovery
        hide-unique-creatives
        hide-saved-filters
        no-search
        :active-filters.sync="activeFilters"
        :selected-date-range.sync="selectedDateRange"
        :sort-order.sync="sortOrder"
        :text-search.sync="textSearch"
        :custom-filter-categories="[partnersFilterCategory]"
        :bar-x-padding="16"
        disable-mount-animation
        @change="fetchAdvertisements"
      />
    </div>
    <!-- ADS VIEW -->
    <div v-if="currentView === 'ads'" class="relative px-4 pt-2 pb-4 fade-in" :class="{ 'overflow-hidden': locked }"
    :style="{ maxHeight: locked && advertisements.length ? 'calc(100vh + 216px)' : 'auto'}">
      <AdvertisementList v-if="advertisements.length"
        discovery
        :advertisements="advertisements"
        :loading="loadingAdvertisements"
        :allAdsLoaded="allAdsLoaded"
        :parent-partner-brand="brand"
        :lock-ads="locked"
        hide-creative-count
        @updateAds="updateAdsLocal"
        @getMoreAds="() => getMoreAds({})"
      />
      <SpyderEmptyState
        v-else-if="!loadingAdvertisements"
        class="mt-32"
        title="No partnership ads yet"
        :description="`Partnership ads run from a creator's handle instead of the brand's, which is exactly why they're easy to miss. The moment ${brand?.name ?? 'this brand'} launches one, it shows up here.`"
        :filters-applied="filtersApplied"
        :has-search-query="textSearch.length > 0"
        :show-brands-link="false"
        @clearFilters="clearFilters"
      />
      <BaseLoadingLogo v-if="loadingAdvertisements && !advertisements.length" />
      <AdsLockedOverlay 
        v-if="locked && advertisements.length" 
        class="absolute bottom-0 left-0 right-0 z-10" style="height: 500px"
        :title="`Start Tracking ${brand?.name || 'this brand'} to see Partnership Ads`"
        description="Search and filter the ads this brand is running in partnership with other pages."
      />
      <infinite-loading
        v-if="advertisements.length && !locked"
        :identifier="infiniteId"
        @infinite="getMoreAds"
      >
        <div slot="spinner">
          <BaseLoadingLogo :margin="2" />
        </div>
        <div slot="no-more" />
        <div slot="no-results" />
      </infinite-loading>
    </div>
    <!-- PARTNERS VIEW -->
    <div v-if="currentView === 'partners'" class="relative creator-list-container pt-3 px-4 pb-12"
    :style="{ height: locked ? 'calc(100vh - 192px)' : 'auto' }" :class="{ 'overflow-hidden': locked }">
      <PreviewLockedOverlay 
        v-if="locked"
        class="absolute top-0 left-0 w-full h-full z-10"
        use-backdrop show-button-icon :vertical-offset="64"
        :title="`Start Tracking ${brand?.name || 'this brand'} to Unlock Brand Partners`"
        description="See which creators are running ads in partnership with this brand."
      />
      <template v-if="loadingPartners">
        <div v-for="i in 30" :key="`partner-skeleton-${i}`" class="w-full h-56 skeleton rounded-xl" style="max-width: 460px;" />
      </template>
      <template v-else>
        <BaseDynamicLoader v-for="(partner, index) in formattedPartners" :key="`partner-${partner.advertiserId}`"
        :identifier="`partner-${partner.advertiserId}`" :buffer="400">
          <div class="w-full flex flex-col p-4 rounded-xl border border-border-normal min-w-0">
            <div class="w-full flex items-center gap-3 min-w-0 mb-5">
              <img v-if="partner.avatar && !failedImages[index]" :src="partner.avatar" class="w-11 h-11 rounded-full flex-shrink-0 object-cover"
              @error="handleImageError(index)">
              <DefaultAvatarIcon v-else :width="44" :height="44" />
              <div class="flex flex-col gap-1 justify-center flex-shrink min-w-0 mr-auto">
                <BaseText type="label" size="sm" class="text-text-muted truncate">
                  {{ partner.displayName }}
                </BaseText>
                <BaseText v-if="partner.handle" type="body" size="sm" class="text-text-normal truncate">
                  @{{ partner.handle }}
                </BaseText>
              </div>
              <a v-if="partner.profileUri" :href="partner.profileUri" target="_blank" class="ml-4 flex items-center gap-0.5 p-1 
              rounded-lg border border-border-normal transition-colors duration-100 hover:bg-neutral-50 flex-shrink-0" style="height: 30px;">
                <component v-if="partner.linkIcon" :is="partner.linkIcon" class="text-text-subdued" />
                <svg v-if="partner.linkIcon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M8.20005 6.3999H13.6V11.7999M13.6 6.3999L6.55005 13.4499" stroke="#808899" 
                  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <OpenExternalIcon v-else class="text-text-subdued" />
              </a>
            </div>
            <div class="w-full grid grid-cols-3 gap-5">
              <div v-for="(metric, metricIndex) in partner.cardMetrics" :key="`metric-${partner.advertiserId}-${metricIndex}`" class="flex flex-col gap-1">
                <div class="flex items-center gap-1.5">
                  <svg v-if="metric.dotColor" xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4" fill="none">
                    <circle cx="2" cy="2" r="2" :fill="metric.dotColor"/>
                  </svg>
                  <BaseText type="label" size="sm" class="text-text-muted">
                    {{ metric.value }}
                  </BaseText>
                </div>
                <BaseText type="body" size="sm" class="text-text-normal">
                  {{ metric.name }}
                </BaseText>
              </div>
            </div>
            <button class="w-max flex items-center gap-0.5 p-1 rounded-md transition-colors duration-100 hover:bg-neutral-25 mt-4"
            @click="seePartnerAds(partner.advertiserId)">
              <BaseText type="label" size="sm" class="text-text-muted">
                View partner ads
              </BaseText>
              <ChevronIcon class="text-text-subdued transform -rotate-90" />
            </button>
          </div>
        </BaseDynamicLoader>
      </template>
    </div>
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import InfiniteLoading from 'vue-infinite-loading'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import { extractSocialHandle, extractSocialDomain } from '@/utils/spyder/brandSocialsUtils'
import formatLabelString from '@/utils/lens/formatGraphLabelString'
import { mixin as clickaway } from 'vue-clickaway2'
import { getBrandPartnersMockData } from '@/utils/spyder/mockDataUtils'

// Components
import AdvertisementList from '../../advertisements/AdvertisementList.vue'
import SpyderEmptyState from '../SpyderEmptyState.vue'
import FilterTopBar from '../../search/FilterTopBar.vue'
import FilterSelectDropdown from '../../filters/FilterSelectDropdown.vue'
import PreviewLockedOverlay from '../PreviewLockedOverlay.vue'
import AdsLockedOverlay from '../AdsLockedOverlay.vue'

// Icons
import AdCardsIcon from '../../globals/Icons/AdCardsIcon.vue'
import PartnersIcon from '../../globals/Icons/SpyderModeIcons/PartnershipAdsIcon.vue'
import DefaultAvatarIcon from '../../globals/Icons/DefaultAvatarIcon.vue'
import MonoMetaIcon from '../../globals/Icons/socials/mono/MonoMetaIcon.vue'
import MonoInstagramIcon from '../../globals/Icons/socials/mono/MonoIGStrokeIcon.vue'
import OpenExternalIcon from '../../globals/Icons/OpenExternalIcon.vue'
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'

const PARTNERS_LIMIT = 300
const LOCKED_PARTNERS_LIMIT = 6

export default {
  name: 'PartnershipAdsBrandView',
  mixins: [clickaway],
  components: {
    AdvertisementList,
    SpyderEmptyState,
    FilterTopBar,
    FilterSelectDropdown,
    PreviewLockedOverlay,
    AdsLockedOverlay,
    InfiniteLoading,
    AdCardsIcon,
    PartnersIcon,
    DefaultAvatarIcon,
    OpenExternalIcon,
    ChevronIcon
  },
  props: {
    brand: {
      type: Object,
      required: true
    },
    // Mirrors SpyderView's overage modal state; gates pagination while active. (See AdLibraryBrandView)
    blocked: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      currentView: 'ads',

      // Data States
      advertisements: [],
      partners: [],

      // Pagination States
      lastDocId: null,
      infiniteId: +new Date(),

      // Loading States
      loadingAdvertisements: true,
      loadingPartners: true,
      allAdsLoaded: false,

      // Filter Bar States
      textSearch: '',
      activeFilters: {},
      selectedDateRange: {
        start: null,
        end: null
      },
      sortOrder: [{ name: 'Newest', value: 'desc' }],

      failedImages: {},
      showPartnerSelectionDropdown: false,
    }
  },
  watch: {
    brand: {
      handler (brand) {
        if (!brand?.id) return
        this.fetchAdvertisements()
        this.fetchPartners()
      },
      immediate: true
    }
  },
  computed: {
    locked () {
      const isSubscribed = this.brand?.is_subscribed
      return isSubscribed === undefined ? false : !isSubscribed
    },
    viewToggles () {
      return [
        { 
          name: 'Ads',
          value: 'ads',
          icon: AdCardsIcon,
          active: this.currentView === 'ads'
        },
        { 
          name: 'Partners',
          value: 'partners',
          icon: PartnersIcon,
          active: this.currentView === 'partners'
        }
      ]
    },
    formattedPartners () {
      const partners = this.locked ? getBrandPartnersMockData().data : this.partners
      return this.formatPartnersData(partners)
    },
    partnerSuggestions () {
      const activePartnerFilters = this.activeFilters['or.partner_creators'] || []
      // When locked - we still show a subset of the real partners data to entice the user
      const partners = this.formatPartnersData(this.partners)
      return partners.filter(partner => !activePartnerFilters.includes(partner.advertiserId)).slice(0, 5)
    },
    partnersFilterCategory () {
      // Based on the schema that can be derived from filterOptions.js
      return {
        id: 'partner_creators',
        name: 'Partners',
        products: ['spyder'],
        searchPlaceholder: 'Search Partners...',
        icon: PartnersIcon,
        serverKey: { or: 'orPartners', and: 'andPartners' },
        optionValueKey: 'advertiserId',
        valueTransform: (value) => value,
        disableExclude: true,
        options: this.formattedPartners.map(partner => ({
          name: partner.displayName,
          advertiserId: partner.advertiserId,
          image: partner.avatar
        })),
        scrollChunkSize: 50
      }
    },
    filtersApplied () {
      return Object.keys(this.activeFilters).length > 0 || this.textSearch.length > 0
    },
    showNoPartnersMessage () {
      return !this.loadingAdvertisements && this.advertisements.length === 0 && !this.filtersApplied
    },
    morePartnersButtonClasses () {
      if (this.locked) return 'bg-neutral-25 cursor-default'
      let classes = 'border border-border-normal'
      classes += this.showPartnerSelectionDropdown 
        ? 'border-border-hover bg-neutral-25' 
        : 'hover:border-border-hover hover:bg-neutral-25'
      return classes
    }
  },
  mounted () {
    EventBus.$on('spyder:scroll', this.handleScroll)
  },
  beforeDestroy () {
    EventBus.$off('spyder:scroll', this.handleScroll)
  },
  methods: {
    async fetchAdvertisements (load = true, refresh = false) {
      if (!this.brand?.id) return
      this.loadingAdvertisements = load
      this.advertisements = []
      this.allAdsLoaded = false

      if (refresh) {
        this.lastDocId = null
      }
      try {
        const { data: results, nextPage } = await ForeplayAPI.Ads.getSpyderPartnershipAds(
          this.$route.params.id,
          null,
          this.buildFilters()
        )

        this.lastDocId = nextPage
        this.advertisements = results
      } catch (e) {
        console.log(e)
      } finally {
        this.loadingAdvertisements = false
        this.infiniteId += 1
      }
    },
    async getMoreAds ($state) {
      if (this.blocked || !this.brand?.id || this.locked) {
        return
      }
      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        this.allAdsLoaded = true
        return
      }

      const { data: results, nextPage } = await ForeplayAPI.Ads.getSpyderPartnershipAds(
        this.$route.params.id,
        this.lastDocId,
        this.buildFilters()
      )
      this.lastDocId = nextPage
      this.advertisements = [...this.advertisements, ...results]

      if (!this.lastDocId) {
        this.allAdsLoaded = true
        if (Object.keys($state).length) {
          $state.complete()
        }
        return
      }

      if (Object.keys($state).length) {
        $state.loaded()
      }
    },
    async fetchPartners () {
      if (!this.brand?.id) return
      this.loadingPartners = true
      this.failedImages = {}
      this.partners = []
      try {
        const partnersResponse = await ForeplayAPI.Brands.getBrandPartners(
          this.$route.params.id,
          { limit: this.locked ? LOCKED_PARTNERS_LIMIT : PARTNERS_LIMIT, page: 1 }
        )
        this.partners = partnersResponse.data ?? []
      } catch (error) {
        console.error(error)
      } finally {
        this.loadingPartners = false
      }
    },
    formatPartnersData (partners) {
      return partners.map(partner => {
        const liveAdsCount = partner.live_ads_count ?? 0
        const adsCount = partner.ads_count ?? 0
        const inactiveAdsCount = Math.max(adsCount - liveAdsCount, 0)
        const domain = extractSocialDomain(partner.profile_url)

        return {
          advertiserId: partner.advertiser_id,
          displayName: partner.current_name ?? partner.name ?? 'Name Unavailable',
          avatar: partner.profile_picture,
          profileUri: partner.profile_url,
          domain,
          handle: extractSocialHandle(partner.profile_url),
          linkIcon: domain === 'facebook' ? MonoMetaIcon : domain === 'instagram' ? MonoInstagramIcon : null,
          cardMetrics: [
            { name: 'Active ads', value: liveAdsCount.toLocaleString(), dotColor: '#40C4AA' },
            { name: 'Inactive ads', value: inactiveAdsCount.toLocaleString(), dotColor: '#C1C7D0' },
            ...(partner.like_count ? [{ name: 'Post likes', value: formatLabelString(partner.like_count, 'number'), dotColor: null }] : [])
          ]
        }
      })
    },
    buildFilters () {
      const ranBetweenStart = +new Date(this.selectedDateRange?.start) || null
      const ranBetweenEnd = +new Date(this.selectedDateRange?.end) || null

      return {
        ...this.activeFilters,
        ranBetweenStart,
        ranBetweenEnd,
        textSearch: this.textSearch,
        sort: this.sortOrder[0].value
      }
    },
    async clearFilters () {
      const filterTopBar = this.$refs.filterTopBar
      filterTopBar.clearAllFilters()
      this.textSearch = ''

      await this.fetchAdvertisements()

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 100)
    },
    addPartnerFilter (advertiserId) {
      if (this.locked) return
      const existing = this.activeFilters['or.partner_creators'] || []
      if (existing.includes(advertiserId)) return
      this.activeFilters = {
        ...this.activeFilters,
        'or.partner_creators': [...existing, advertiserId]
      }
      this.fetchAdvertisements()
    },
    seePartnerAds (advertiserId) {
      if (this.locked) return
      this.activeFilters = { 'or.partner_creators': [advertiserId] }
      this.currentView = 'ads'
      this.fetchAdvertisements()
    },
    applyFilterChanges (filters) {
      if (this.locked) return
      this.activeFilters = { ...filters }
      this.showPartnerSelectionDropdown = false
      this.fetchAdvertisements()
    },
    // ================= UI METHODS =================

    updateAdsLocal (adsToUpdate) {
      const duplicateAds = [...this.advertisements]
      adsToUpdate.forEach((ad) => {
        const index = duplicateAds.findIndex((a) => a.id === ad.id)
        if (index !== -1) {
          duplicateAds[index] = ad
        }
      })
      this.advertisements = duplicateAds
    },
    handleScroll (position) {
      const bar = this.$refs.filterBar
      if (!bar) return

      const marginValue = position.scrollTop / 6 < 16 ? position.scrollTop / 6 : 16
      const rounding = position.scrollTop / 10 < 5 ? position.scrollTop / 10 : 5

      bar.style.marginLeft = `${marginValue}px`
      bar.style.marginRight = `${marginValue}px`
      bar.style.marginTop = `${marginValue}px`
      bar.style.borderRadius = `${rounding}px`

      const shadowSection = (maxValue) => {
        return position.scrollTop / 15 < maxValue ? position.scrollTop / 15 : maxValue
      }

      if (position.scrollTop) {
        bar.style.boxShadow = `rgba(0, 0, 0, 0.08) 0px ${shadowSection(
          15
        )}px ${shadowSection(55)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
          7
        )}px ${shadowSection(
          10
        )}px, rgba(0, 0, 0, 0.08) 0px 4px 6px, rgba(0, 0, 0, 0.08) 0px ${shadowSection(
          7
        )}px ${shadowSection(13)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
          3
        )}px ${shadowSection(5)}px`
      } else {
        bar.style.boxShadow = null
      }
    },
    handleImageError (index) {
      this.$set(this.failedImages, index, true)
    },
  }
}
</script>

<style scoped>
.spyder-filter-bar {
  position: sticky;
  top: 16px;
  z-index: 40000;
}

.toggle-atom {
  background-color: transparent;
  box-shadow: none;
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.toggle-atom:hover:not(.active) {
  background-color: #ECEFF3;
}
.toggle-atom.active {
  background-color: white;
  box-shadow: 0 3px 3px -1.5px rgba(6, 7, 16, 0.04), 0 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.08), 0 0.25px 1.5px 0.75px rgba(6, 7, 16, 0.04), 0 0 0 0.5px rgba(6, 7, 16, 0.02);
}

.creator-list-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.more-partners-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0px;
  z-index: 40001;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-to, .v-enter, .v-leave-from {
  opacity: 1;
}
.v-leave-to, .v-enter-from {
  opacity: 0;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
